import type { MobyDB } from "@/moby/db";
import { bytesToHex, hexToBytes } from "@noble/curves/abstract/utils";
import { ed25519 } from "@noble/curves/ed25519";

export type EmailGatewaySyncConfig = {
  baseUrl: string;
  address: string;
  secret: string;
  pollIntervalMs?: number;
};

type PendingDeliveryDto = {
  id: string;
  recipientAddress: string;
  senderAddress: string;
  messageType: string;
  hasAttachment: boolean;
  attachmentRootHash?: string;
  fileName?: string;
  fileSize?: number;
  createdAt: string;
  expiresAt: string;
  delivered: boolean;
  deliveredAt?: string | null;
};

type PeerPresenceDto = {
  address: string;
  lastSeen: string;
};

type SyncResponseDto = {
  cursor: string;
  deliveries: PendingDeliveryDto[];
  peerPresence?: PeerPresenceDto[];
};

function normalizeBaseUrl(baseUrl: string) {
  return baseUrl.replace(/\/+$/, "");
}

function parseBool01(value: boolean): 0 | 1 {
  return value ? 1 : 0;
}

function toArrayBufferBackedU8(data: Uint8Array): Uint8Array<ArrayBuffer> {
  const buffer = new ArrayBuffer(data.byteLength);
  const out = new Uint8Array(buffer);
  out.set(data);
  return out;
}

async function sha256Hex(data: Uint8Array): Promise<string> {
  const normalized = toArrayBufferBackedU8(data);
  const digest = await crypto.subtle.digest(
    "SHA-256",
    normalized.buffer as ArrayBuffer,
  );
  return bytesToHex(new Uint8Array(digest));
}

export class EmailGatewaySync {
  private readonly db: MobyDB;
  private readonly config: Required<EmailGatewaySyncConfig>;
  private timer: number | undefined;
  private running = false;

  public constructor(db: MobyDB, config: EmailGatewaySyncConfig) {
    this.db = db;

    this.config = {
      ...config,
      baseUrl: normalizeBaseUrl(config.baseUrl),
      pollIntervalMs: config.pollIntervalMs ?? 15_000,
    };
  }

  public start(signal?: AbortSignal) {
    if (this.running) return;
    this.running = true;

    const tick = async () => {
      if (!this.running) return;
      if (signal?.aborted) {
        this.stop();
        return;
      }

      try {
        await this.syncOnce(signal);
      } catch (error) {
        console.warn("[EMAIL_GATEWAY_SYNC] syncOnce failed", error);
      } finally {
        if (!this.running) return;
        this.timer = window.setTimeout(tick, this.config.pollIntervalMs);
      }
    };

    void tick();
  }

  public stop() {
    this.running = false;
    if (this.timer !== undefined) {
      window.clearTimeout(this.timer);
      this.timer = undefined;
    }
  }

  private async getSyncCursor(): Promise<string> {
    const row = await this.db.emailGatewaySyncState.get("pending_deliveries_cursor");
    return row?.value ?? "";
  }

  private async setSyncCursor(cursor: string): Promise<void> {
    await this.db.emailGatewaySyncState.put({
      key: "pending_deliveries_cursor",
      value: cursor,
      updatedAt: new Date(),
    });
  }

  private async signedFetch(path: string, init: RequestInit, signal?: AbortSignal) {
    const url = `${this.config.baseUrl}${path}`;

    const method = (init.method ?? "GET").toUpperCase();
    const timestamp = Date.now().toString();

    let bodyBytes: Uint8Array = new Uint8Array();
    if (typeof init.body === "string") {
      bodyBytes = new TextEncoder().encode(init.body);
    } else if (init.body instanceof Uint8Array) {
      bodyBytes = toArrayBufferBackedU8(init.body);
    } else if (init.body instanceof ArrayBuffer) {
      bodyBytes = new Uint8Array(init.body);
    } else if (init.body instanceof Blob) {
      bodyBytes = new Uint8Array(await init.body.arrayBuffer());
    }

    const bodyHash = await sha256Hex(bodyBytes);
    const signaturePayload = new TextEncoder().encode(
      `${method}\n${path}\n${timestamp}\n${bodyHash}`,
    );

    const signature = ed25519.sign(signaturePayload, hexToBytes(this.config.secret));

    const headers = new Headers(init.headers);
    headers.set("content-type", headers.get("content-type") ?? "application/json");
    headers.set("x-moby-address", this.config.address);
    headers.set("x-moby-timestamp", timestamp);
    headers.set("x-moby-signature", bytesToHex(signature));

    return await fetch(url, {
      ...init,
      method,
      headers,
      signal,
      cache: "no-store",
    });
  }

  public async syncOnce(signal?: AbortSignal) {
    const cursor = await this.getSyncCursor();

    const query = new URLSearchParams({ recipient: this.config.address });
    if (cursor) query.set("cursor", cursor);

    const response = await this.signedFetch(
      `/v1/sync/pending-deliveries?${query.toString()}`,
      { method: "GET" },
      signal,
    );

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(
        `[EMAIL_GATEWAY_SYNC] HTTP ${response.status} ${response.statusText} ${text}`,
      );
    }

    const dto = (await response.json()) as SyncResponseDto;

    await this.db.transaction(
      "rw",
      this.db.emailGatewayPendingDeliveries,
      this.db.emailGatewayPeerPresence,
      this.db.emailGatewaySyncState,
      async () => {
        if (dto.deliveries.length > 0) {
          const deliveries = dto.deliveries.map((d) => {
            return {
              id: d.id,
              recipientAddress: d.recipientAddress,
              senderAddress: d.senderAddress,
              messageType: d.messageType,
              hasAttachment: parseBool01(d.hasAttachment),
              attachmentRootHash: d.attachmentRootHash,
              fileName: d.fileName,
              fileSize: d.fileSize,
              createdAt: new Date(d.createdAt),
              expiresAt: new Date(d.expiresAt),
              delivered: parseBool01(d.delivered),
              deliveredAt: d.deliveredAt ? new Date(d.deliveredAt) : undefined,
            };
          });

          await this.db.emailGatewayPendingDeliveries.bulkPut(deliveries);
        }

        if (dto.peerPresence && dto.peerPresence.length > 0) {
          const presence = dto.peerPresence.map((p) => {
            return {
              address: p.address,
              lastSeen: new Date(p.lastSeen),
            };
          });

          await this.db.emailGatewayPeerPresence.bulkPut(presence);
        }

        await this.setSyncCursor(dto.cursor);
      },
    );
  }
}
